import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider } from "@/context/LanguageContext";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AuthCallback, GoogleCallback } from "@/components/AuthCallback";
import { UpgradeLimitModal } from "@/components/UpgradeLimitModal";
import { SupportChat } from "@/components/SupportChat";
import { Onboarding } from "@/components/Onboarding";
import { WelcomeBanner } from "@/components/WelcomeBanner";
import { ReminderNotifier } from "@/components/ReminderNotifier";
import { Seo } from "@/components/Seo";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Goals from "@/pages/Goals";
import Reminders from "@/pages/Reminders";
import Membership from "@/pages/Membership";
import Chat from "@/pages/Chat";
import Category from "@/pages/Category";
import Calculators from "@/pages/Calculators";
import Guides from "@/pages/Guides";
import Compare from "@/pages/Compare";
import Assistant from "@/pages/Assistant";
import Settings from "@/pages/Settings";
import TrustSources from "@/pages/TrustSources";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import RefundPolicy from "@/pages/RefundPolicy";
import Contact from "@/pages/Contact";
import NotFound from "@/pages/NotFound";

// Route → SEO metadata. Private/auth pages are noindex. Prefix rules cover
// dynamic routes (/category/:slug, /calculators/:tool, /chat/:id).
const SEO_MAP = {
  "/": { title: "Make Every Minute Count", description: "EVERMIN is your AI-powered everyday-life assistant for India — understand, compare, decide and act on money, documents, shopping, jobs and more." },
  "/compare": { title: "Compare Anything", description: "Put two options side by side with EVERMIN's AI compare — pros, cons, running cost and a personalised 'best for you' pick." },
  "/calculators": { title: "Smart Calculators", description: "Free everyday calculators for budgets, EMI/loans, rent-vs-buy and more — instant answers in INR." },
  "/guides": { title: "How-To Guide Library", description: "Simple, practical how-to guides for government work, money, home, career and tech in India." },
  "/membership": { title: "Plans & Pricing", description: "EVERMIN plans: Free (10 AI/day), Basic ₹199 (30/day), Plus ₹299 (60/day) and Pro ₹499 (100/day)." },
  "/trust": { title: "Trust & Sources", description: "How EVERMIN keeps answers honest, verifies current information and shows sources you can check." },
  "/privacy": { title: "Privacy Policy", description: "How EVERMIN handles your data and protects your privacy." },
  "/terms": { title: "Terms of Use", description: "The terms for using EVERMIN's everyday-life AI assistant." },
  "/refund-policy": { title: "Refund & Cancellation Policy", description: "How EVERMIN membership cancellations and refunds work for the Basic, Plus and Pro plans." },
  "/contact": { title: "Contact", description: "Questions or feedback? Get in touch with the EVERMIN team." },
};
const NOINDEX_PREFIXES = ["/dashboard", "/goals", "/reminders", "/settings", "/chat", "/login", "/signup", "/forgot-password", "/reset-password", "/assistant"];

function seoForPath(pathname) {
  if (SEO_MAP[pathname]) return SEO_MAP[pathname];
  if (pathname.startsWith("/calculators")) return SEO_MAP["/calculators"];
  if (pathname.startsWith("/category/")) return { title: "Everyday help by category", description: "Guides, calculators and trusted sources for everyday life, organised by category." };
  if (NOINDEX_PREFIXES.some((p) => pathname.startsWith(p))) return { title: "EVERMIN", description: "Your personal EVERMIN.", noindex: true };
  return { title: "Page not found", description: "The page you're looking for doesn't exist.", noindex: true };
}

function AppRouter() {
  const location = useLocation();

  // Scroll to the top on every route change so users land at the start of the
  // new page (fixes the "stayed at previous scroll position" navigation bug).
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  // Handle Emergent Google OAuth callback before any protected route runs.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  const seo = seoForPath(location.pathname);

  return (
    <>
      <Seo title={seo.title} description={seo.description} noindex={seo.noindex} />
      <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/google" element={<GoogleCallback />} />
      <Route path="/membership" element={<Membership />} />
      <Route path="/category/:slug" element={<Category />} />
      <Route path="/calculators" element={<Calculators />} />
      <Route path="/calculators/:tool" element={<Calculators />} />
      <Route path="/guides" element={<Guides />} />
      <Route path="/compare" element={<Compare />} />
      <Route path="/assistant" element={<Assistant />} />
      <Route path="/trust" element={<TrustSources />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/contact" element={<Contact />} />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <Chat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat/:id"
        element={
          <ProtectedRoute>
            <Chat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/goals"
        element={
          <ProtectedRoute>
            <Goals />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reminders"
        element={
          <ProtectedRoute>
            <Reminders />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <UpgradeLimitModal />
          <SupportChat />
          <Onboarding />
          <WelcomeBanner />
          <ReminderNotifier />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
